// © 2026 Buddy Media LLC. All rights reserved.
import { NavLink, Outlet } from "react-router-dom";
import {
  Activity,
  Bot,
  Cpu,
  KeyRound,
  Layers,
  LogOut,
  Mail,
  Map,
  ScrollText,
  Server,
  Shield,
  Sparkles,
  Wallet,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";

// T-615 Phase 2 — full migrated leaf set. Order matches the spec's
// "low-risk → high-risk" migration order so operators see the
// safest views at the top.
const NAV = [
  { to: "/cluster-pulse", label: "Cluster pulse", icon: Activity },
  { to: "/credentials", label: "Cluster credentials", icon: KeyRound },
  { to: "/q-stream", label: "Q stream", icon: ScrollText },
  { to: "/token-budget", label: "Token budget", icon: Wallet },
  { to: "/cluster-topology", label: "Cluster topology", icon: Map },
  { to: "/initiative-digests", label: "Initiative digests", icon: Mail },
  { to: "/service-accounts", label: "Service accounts", icon: Shield },
  { to: "/ops-agents", label: "Ops agents", icon: Bot },
  { to: "/merge-daemon", label: "Merge daemon", icon: Layers },
  { to: "/shadow-orchestrator", label: "Shadow Orchestrator", icon: Sparkles },
  { to: "/secrets", label: "Operational secrets", icon: Server },
  { to: "/backend-worker", label: "Backend worker", icon: Cpu },
  { to: "/frontend-worker", label: "Frontend worker", icon: Cpu },
  { to: "/wire-test-worker", label: "Wire-test worker", icon: Cpu },
];

export function AppShell() {
  const setBearer = useAuthStore((s) => s.setBearer);
  return (
    <div className="flex min-h-screen">
      <aside
        className="w-60 border-r"
        style={{ borderColor: "var(--bw-border)" }}
      >
        <div className="px-4 py-5">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
              }}
            />
            <span className="text-sm font-semibold text-white">
              IA Cluster Admin
            </span>
          </div>
          <p className="mt-1 text-xs" style={{ color: "var(--bw-muted)" }}>
            Internal admin · cluster ops
          </p>
        </div>
        <nav className="px-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-white/[0.04] text-white"
                      : "text-slate-400 hover:bg-white/[0.02] hover:text-slate-200"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={() => setBearer(null)}
          className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-rose-300"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </aside>
      <main className="flex-1 px-8 py-6">
        <Outlet />
      </main>
    </div>
  );
}
